// PIMS_FAQ ADK Agent — deployed to Cloud Run as `tg-faq-agent-service`, independent
// from the FastAPI CRUD service. See Ref/faq_admin_integration_plan_20260810.md.
export const AGENT_API =
  process.env.REACT_APP_AGENT_API_URL ||
  "https://tg-faq-agent-service-7ear5wpkgq-uc.a.run.app";

const APP_NAME = "agent";

// stage 欄位(2026-08-10): the backend now emits a semantic `stage` value directly in
// the SSE event's stateDelta (see PIMS_FAQ's agent/workflows/routing.py, root.py,
// website_intro.py). We only read this field to drive the UI's stage indicator —
// we do NOT parse the Agent's internal node names (nodeInfo.path/event.author) to
// infer progress. Those node names are backend implementation detail and are not a
// stable contract (see Ref/faq_chat_demo_interface_principle_20260810.md).
// The few remaining nodeInfo.path reads below (db_log_node/apply_verdict_node/
// faq_hybrid_search_node) are NOT stage inference — they extract the final output
// payload of a specific terminal node, which is a different concern.
const HIT_SYNTHESIS_AUTHOR = "db_synthesis_node";

function randomId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

// verbosity/groundingModel/thinking are optional session-creation params
// (flat keys, not nested under state — see Ref/frontend_change_requests_en.md
// items 2-3 and Ref/04_frontend_changes.md item 3). Omitted, empty, or
// unrecognised values safely fall back to the backend's defaults, so we only
// send them when the caller actually picked something.
//
// sessionId: pass one to reopen a conversation continued from History under its
// own id, so the agent's session and the saved conversation keep sharing it. ADK
// refuses to create a session that already exists; that is the agent still
// having it, so it counts as success.
export async function createChatSession(
  token,
  userId,
  { verbosity, groundingModel, thinking, sessionId: existingId } = {},
) {
  const sessionId = existingId || randomId("session");
  const body = {};
  if (verbosity) body.verbosity = verbosity;
  if (groundingModel) body.grounding_model = groundingModel;
  if (thinking) body.thinking = thinking;
  const response = await fetch(
    `${AGENT_API}/apps/${APP_NAME}/users/${userId}/sessions/${sessionId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify(body),
    },
  );
  const alreadyExists =
    existingId && (response.status === 400 || response.status === 409);
  if (!response.ok && !alreadyExists) {
    throw new Error(`Failed to create chat session: ${response.status}`);
  }
  return sessionId;
}

// handlers: { onStageChange, onUnderstoodQuery, onSourcesReady, onTextDelta, onDone, onError }
export async function streamChatMessage(
  token,
  userId,
  sessionId,
  message,
  handlers,
) {
  const response = await fetch(`${AGENT_API}/run_sse`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify({
      appName: APP_NAME,
      userId,
      sessionId,
      newMessage: { role: "user", parts: [{ text: message }] },
      streaming: true,
    }),
  });

  if (!response.ok || !response.body) {
    handlers.onError(new Error(`Agent request failed: ${response.status}`));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // Station 1 (FAQ) state.
  let matchedFaqIds = [];
  let hybridResults = [];
  let hitFinalized = false;

  let done = false;
  let stage = "understanding";

  function setStage(next) {
    if (stage === next) return;
    stage = next;
    handlers.onStageChange(next);
  }

  function toWebSources(raw) {
    return raw.map((s, i) => ({
      index: i + 1,
      title: s.title,
      domain: s.domain,
      url: s.url,
      image: s.image ?? null,
      description: s.description ?? null,
      enrichmentStatus: s.enrichment_status ?? "not_attempted",
    }));
  }

  try {
    while (true) {
      const { value, done: readerDone } = await reader.read();
      if (readerDone) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const payload = line.slice("data:".length).trim();
        if (!payload) continue;

        let event;
        try {
          event = JSON.parse(payload);
        } catch {
          continue;
        }

        const path = event.nodeInfo?.path ?? "";

        if (path.includes("faq_hybrid_search_node") && event.output?.results) {
          hybridResults = event.output.results;
        }

        const delta = event.actions?.stateDelta;
        if (delta?.rewritten_query) {
          handlers.onUnderstoodQuery(delta.rewritten_query);
        }
        if (delta?.source === "db") {
          matchedFaqIds = delta.matched_faq_ids ?? [];
        }
        // Backend emits a semantic stage value directly — see file header.
        if (delta?.stage) {
          setStage(delta.stage);
        }

        // Station 1 hit: stream db_synthesis_node's text immediately — hit is a
        // short-circuit terminal, nothing downstream can override this answer.
        if (event.partial && event.author === HIT_SYNTHESIS_AUTHOR) {
          const text =
            event.content?.parts?.map((p) => p.text ?? "").join("") ?? "";
          if (text) handlers.onTextDelta(text);
        }
        // db_log_node is the terminal node of the hit path — its appearance means
        // the hit path is fully done, no station 2/3 events will follow.
        if (path.includes("db_log_node") && !hitFinalized && !done) {
          hitFinalized = true;
          done = true;
          // Snapshot into a block-scoped const so the callbacks below don't
          // close over the loop-mutated `hybridResults`.
          const results = hybridResults;
          const matchedFaqs = matchedFaqIds
            .map((id) => results.find((r) => r.question_id === id))
            .filter((r) => r !== undefined)
            .map((r) => ({
              questionId: r.question_id,
              displayId: r.display_id,
              question: r.question,
              sourceUrl: r.source_url,
            }));
          handlers.onDone({ source: "db", matchedFaqs, webSources: [] });
        }

        // "comparing" stage marks the terminal node of the miss path — its output is
        // the final chosen candidate answer {answer, sources}. We match on stage, not
        // node name: the node ending this path has changed before (apply_verdict_node
        // -> log_open_web_only_node) and stage is the stable contract across that.
        // The 400ms pause before "composing" is intentional: without it, the
        // "comparing" stage gets overwritten in the same JS call stack before the UI
        // ever renders it.
        if (delta?.stage === "comparing" && !done) {
          done = true;
          await new Promise((resolve) => setTimeout(resolve, 400));
          setStage("composing");
          const verdict = event.output;
          const sources = toWebSources(verdict?.sources ?? []);
          const source = verdict?.source ?? "web_search";
          if (sources.length > 0) handlers.onSourcesReady(source, sources);
          if (verdict?.answer) handlers.onTextDelta(verdict.answer);
          handlers.onDone({ source, matchedFaqs: [], webSources: sources });
        }
      }
    }

    if (!done) {
      // Stream ended without hitting an expected terminal node — shouldn't happen,
      // this is a defensive fallback.
      handlers.onDone({ source: "db", matchedFaqs: [], webSources: [] });
    }
  } catch (error) {
    handlers.onError(error instanceof Error ? error : new Error(String(error)));
  }
}
